import {
  useMutation,
  type UseMutationOptions,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { type AxiosResponse } from 'axios';

import { createModel, deleteModelById, updateModel } from '../../api';
import {
  cloneModel,
  type CloneModelInput,
  type CreateModelInput,
  type DeleteModelInput,
  exportModelOutput,
  type ExportModelOutputInput,
  getModelById,
  type GetModelByIdInput,
  getModelComparisonIsRunning,
  type GetModelComparisonIsRunningInput,
  getModelComparisonOutput,
  type GetModelComparisonOutputInput,
  getModelIsRunning,
  type GetModelIsRunningInput,
  getModelOutput,
  type GetModelOutputInput,
  getModelOutputWithActualData,
  type GetModelOutputWithActualDataInput,
  getModels,
  type GetModelsInput,
  runModelComparison,
  type RunModelComparisonInput,
  runModelOutput,
  type RunModelOutputInput,
  type UpdateModelInput,
} from '../../api/models';
import { type TypeModel } from '../../common/dataTypes';
import { type CompareCellType } from '../../components/DataGridPremiumTable';
import { downloadFile, getCompositionFileName } from '../../utils/helpers';
import { type DefaultMutationError, type DefaultQueryError } from '../index';

const createModelFn = async (data: CreateModelInput) => {
  const { data: model }: AxiosResponse<TypeModel> = await createModel(data);

  return model;
};

const updateModelFn = async (data: UpdateModelInput) => {
  const { data: model }: AxiosResponse<TypeModel> = await updateModel(data);

  return model;
};

const deleteModelFn = async (data: DeleteModelInput) => {
  const { data: model }: AxiosResponse = await deleteModelById(data);

  return model;
};

export const useDeleteModel = (
  opts?: Partial<
    UseMutationOptions<
      Awaited<ReturnType<typeof deleteModelFn>>,
      DefaultMutationError,
      any,
      any
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['deleteModelFn'],
    mutationFn: (input: DeleteModelInput) => deleteModelFn(input),
    onSuccess(data, variables, unknown, context) {
      queryClient.refetchQueries({
        queryKey: ['getModels'],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
  });
};

export const useUpdateModel = (
  opts?: Partial<
    UseMutationOptions<
      Awaited<ReturnType<typeof updateModelFn>>,
      DefaultMutationError,
      any,
      any
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['updateModelFn'],
    mutationFn: (input: UpdateModelInput) => updateModelFn(input),
    onSuccess: async (data, variables, unknown, context) => {
      await queryClient.refetchQueries({
        queryKey: ['getModels'],
      });
      await queryClient.refetchQueries({
        queryKey: [
          'getModelById',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
            modelId: variables.modelId,
          },
        ],
      });
      await queryClient.refetchQueries({
        queryKey: [
          'getDatasets',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
          },
        ],
      });
      await queryClient.refetchQueries({
        queryKey: [
          'getSharedModels',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
          },
        ],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });
};

export const useGetModelOutput = (
  input: GetModelOutputInput,
  opts?: Partial<
    UseQueryOptions<
      Awaited<Record<string, Record<string, number[] | string[]>>>,
      DefaultQueryError
    >
  >
) =>
  useQuery({
    ...opts,
    enabled: !!input.orgId && !!input.projectId && (opts?.enabled || true),
    queryKey: ['getModelOutput', input],
    queryFn: async () => {
      const {
        data: model,
      }: AxiosResponse<Record<string, Record<string, number[]>>> =
        await getModelOutput(input);

      return model;
    },
  });

export const useGetModelOutputWithActualData = (
  input: GetModelOutputWithActualDataInput,
  opts?: Partial<
    UseQueryOptions<
      Awaited<Record<string, Record<string, number[] | string[]>>>,
      DefaultQueryError
    >
  >
) =>
  useQuery({
    enabled: !!input.orgId && !!input.projectId && (opts?.enabled || true),
    queryKey: ['getModelOutputWithActualData', input],
    queryFn: async () => {
      const {
        data: model,
      }: AxiosResponse<Record<string, Record<string, number[]>>> =
        await getModelOutputWithActualData(input);

      return model;
    },
    ...opts,
  });

export const useGetModelIsRunning = (
  input: GetModelIsRunningInput,
  opts?: Partial<UseQueryOptions<Awaited<boolean>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getModelIsRunning', input],
    queryFn: async () => {
      const { data: model }: AxiosResponse<boolean> =
        await getModelIsRunning(input);

      return model;
    },
    ...opts,
  });

export const useRunModelOutput = (
  opts?: Partial<
    UseMutationOptions<
      Awaited<Record<string, Record<string, number[]>>>,
      DefaultMutationError,
      any,
      any
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['runModelOutput'],
    mutationFn: async (input: RunModelOutputInput) => {
      const { data }: AxiosResponse<Record<string, Record<string, number[]>>> =
        await runModelOutput(input);

      return data;
    },
    onSuccess: async (data, variables, unknown, context) => {
      await opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError: async (error, variables, unknown, context) => {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });
};

export const useCreateModel = (
  opts?: Partial<
    UseMutationOptions<
      Awaited<ReturnType<typeof createModelFn>>,
      DefaultMutationError,
      any,
      any
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['createModelFn'],
    mutationFn: (input: CreateModelInput) => createModelFn(input),
    onSuccess(data, variables, unknown, context) {
      queryClient.refetchQueries({
        queryKey: ['getModels'],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });
};

export const useCloneModel = (
  opts?: Partial<
    UseMutationOptions<
      Awaited<ReturnType<typeof createModelFn>>,
      DefaultMutationError,
      any,
      any
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['cloneModel'],
    mutationFn: async (input: CloneModelInput) => {
      const { data } = await cloneModel(input);

      return data;
    },
    onSuccess: async (data, variables, unknown, context) => {
      await queryClient.refetchQueries({
        queryKey: [
          'getModels',
          {
            orgId: variables.orgId,
            projectId: variables.projectId,
          },
        ],
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
  });
};

export const useGetModels = (
  input: GetModelsInput,
  opts?: Partial<UseQueryOptions<Awaited<TypeModel[]>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getModels', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeModel[]> = await getModels(input);

      return data;
    },
    ...opts,
  });

export const useGetModelById = (
  input: GetModelByIdInput,
  opts?: Partial<UseQueryOptions<Awaited<TypeModel>, DefaultQueryError>>
) =>
  useQuery({
    queryKey: ['getModelById', input],
    queryFn: async () => {
      const { data }: AxiosResponse<TypeModel> = await getModelById(input);

      return data;
    },
    ...opts,
  });

export const useGetModelComparisonOutput = (
  input: GetModelComparisonOutputInput,
  opts?: Partial<
    UseQueryOptions<
      Awaited<
        Record<string, Record<string, (string | number | CompareCellType)[]>>
      >,
      DefaultQueryError
    >
  >
) =>
  useQuery({
    queryKey: ['getModelComparisonOutput', input],
    queryFn: async () => {
      const { data: model } = await getModelComparisonOutput(input);

      return model;
    },
    ...opts,
    enabled: !!input.modelA && !!input.modelB && (opts?.enabled ?? true),
  });

export const useGetModelComparisonIsRunning = (
  input: GetModelComparisonIsRunningInput,
  opts?: Partial<UseQueryOptions<Awaited<boolean>, DefaultQueryError>>
) =>
  useQuery({
    enabled: !!input.orgId && !!input.projectId,
    queryKey: ['getModelComparisonIsRunning', input],
    queryFn: async () => {
      const { data: model }: AxiosResponse<boolean> =
        await getModelComparisonIsRunning(input);

      return model;
    },
    ...opts,
  });

export const useRunModelComparison = (
  opts?: Partial<
    UseMutationOptions<
      Awaited<Record<string, Record<string, number[]>>>,
      DefaultMutationError,
      any,
      any
    >
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['runModelComparison'],
    mutationFn: async (input: RunModelComparisonInput) => {
      const { data } = await runModelComparison(input);

      return data;
    },
    onSuccess: async (data, variables, unknown, context) => {
      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });
};

export const useExportModelOutput = (
  opts?: Partial<
    UseMutationOptions<
      Awaited<{
        fileLink: string;
        filename: string;
      }>,
      DefaultMutationError,
      any,
      any
    >
  >
) =>
  useMutation({
    mutationKey: ['exportModelOutput'],
    mutationFn: async (input: ExportModelOutputInput) => {
      const res = await exportModelOutput(input);

      const contentDisposition = res.headers['content-disposition'];

      const filename = getCompositionFileName(contentDisposition);

      return {
        fileLink: URL.createObjectURL(res.data),
        filename,
      };
    },
    onSuccess: async (data, variables, unknown, context) => {
      const { fileLink, filename } = data;

      downloadFile({
        fileLink,
        filename,
      });
      opts?.onSuccess?.(data, variables, unknown, context);
    },
    onError(error, variables, unknown, context) {
      opts?.onError?.(
        error as unknown as DefaultMutationError,
        variables,
        unknown,
        context
      );
    },
  });
